import React, { useCallback, useMemo } from 'react';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Button, Grid } from '@mui/material';
import Icon from '@mui/material/Icon';

import { useMessageContext } from 'UI/contexts';
import useConfig from 'UI/customHooks/useConfig';
import { useT } from 'UI/lib';

import useDeleteAllAssignedSkillValidations from '../../containers/SkillValidation/AllAssignedSkills/useDeleteAllAssignedSkillValidations';
import BasicTooltip from '../BasicTooltip/BasicTooltip';
import ConfirmationBasicDialog from '../ConfirmationBasicDialog/ConfirmationBasicDialog';
import { useConfirmationDialog } from '../ConfirmationDialog/useConfirmationDialog';
import DisplayStarsExpertise from '../SkillExpertiseManagement/DisplayStarsExpertise';
import ContentConfirmationDialog from './ContentConfirmationDialog';
import SkillExpertiseEmployeePendingTooltip from './SkillExpertiseEmployeePendingTooltip';

const SkillExpertiseEmployee = ({
  suggestedLevel,
  suggestedBy,
  supportedLevels,
  suggested,
  actualLevel,
  skillName,
  handleChangeExpertise,
  skillId,
  comment,
}) => {
  const { isDialogOpen, handleDialogClose, handleDialogOpen } = useConfirmationDialog();
  const t = useT();
  const message = useMessageContext();
  const config = useConfig();
  const skillsPoliciesPreset =
    config.cms?.components?.SelfAssignedSkills?.skillSynonymPoliciesPreset;

  const {
    deleteSkillValidations: deleteSelfAssignedSkillValidations,
  } = useDeleteAllAssignedSkillValidations({ skillsPoliciesPreset });

  const dataConfirmation = useMemo(
    () => [
      {
        skillName,
        suggested: (
          <Grid container>
            <DisplayStarsExpertise actualLevel={suggestedLevel} suggestedLevel={suggestedLevel} />
          </Grid>
        ),
      },
    ],
    [skillName, suggested]
  );

  const handleDialogAction = useCallback(() => {
    handleChangeExpertise(suggested);
    handleDialogClose();
  }, [suggested]);

  const handleDialogReject = useCallback(() => {
    deleteSelfAssignedSkillValidations({
      skillIdsToDelete: skillId,
    }).then(() => {
      message.setMessage({
        type: 'info',
        text: `${t('restrict:skills.message.skill_suggestion_rejected')} ✖`,
      });
    });
    handleDialogClose();
  }, [skillId]);

  return (
    <>
      <BasicTooltip placement="top" arrow title={<SkillExpertiseEmployeePendingTooltip />}>
        <Button
          sx={{
            border: '1px solid #F57C0099',
            backgroundColor: '#FFB74D33',
            borderRadius: '4px',
            padding: '2px 10px',
            justifyContent: 'center',
            marginBottom: '5px',
          }}
          onClick={handleDialogOpen}
        >
          <Grid item container justifyContent="center">
            <Grid item container justifyContent="center">
              {supportedLevels.map((supportedLevel, index) => (
                <Grid
                  key={supportedLevel}
                  aria-label={`suggest skill as ${supportedLevel}`}
                  sx={{ padding: 0, lineHeight: 1 }}
                >
                  <Icon
                    data-test={`star-icon${index}`}
                    sx={{
                      color: '#dd7c00',
                      borderBottom: supportedLevel === suggested ? '1px solid' : 'none',
                    }}
                  >
                    {index <= supportedLevels.indexOf(actualLevel) ? (
                      <StarIcon />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </Icon>
                </Grid>
              ))}
            </Grid>
            <Grid item>{t('restrict:manager_assessment_status.change')}</Grid>
          </Grid>
        </Button>
      </BasicTooltip>
      <ConfirmationBasicDialog
        isOpen={isDialogOpen}
        handleClose={handleDialogClose}
        handleReject={handleDialogReject}
        handleConfirm={handleDialogAction}
        titleText="restrict:skill_expertise_tooltip_employee_new_suggested_expertise_level"
        titleElements={{ name: suggestedBy }}
        contentComponent={
          <ContentConfirmationDialog
            data={dataConfirmation}
            comment={comment}
            managerName={suggestedBy}
          />
        }
        maxWidth="xs"
        cancelButtonText="common:reject"
        fullWidth
      />
    </>
  );
};

export default SkillExpertiseEmployee;
